import React, { useState, useEffect, useRef } from 'react';
import { FormControl, TextField, Autocomplete, CircularProgress, Box, Typography, FormHelperText, IconButton, Tooltip } from '@mui/material';
import { FaPlus } from 'react-icons/fa';
import { organizationApi } from '../../../services/api';
import { useAlert } from '../../../contexts/AlertContext';
import ConfirmationModal from '../ConfirmationModal';
import { COUNTRY_RETURN_TYPES } from '../../../constants/selectorReturnTypes';

// Legacy export for backward compatibility
export const RETURN_VALUE_TYPES = COUNTRY_RETURN_TYPES;

const CountrySelector = ({ 
  value, 
  onChange, 
  label = "Country",
  required = false,
  disabled = false,
  fullWidth = true,
  autoFocus = false,
  returnValue = COUNTRY_RETURN_TYPES.ID, // COUNTRY_RETURN_TYPES.ID or COUNTRY_RETURN_TYPES.NAME
  onOpen,
  onClose,
  onCreatingCountry, // New callback to notify parent about country creation state
  ...props 
}) => {
  const [countries, setCountries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingCountryName, setPendingCountryName] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [isAboutToCreateCountry, setIsAboutToCreateCountry] = useState(false);
  const { showAlert } = useAlert();
  const inputRef = useRef(null);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setIsLoading(true);
        const countriesData = await organizationApi.getCountries();
        setCountries(countriesData);
      } catch (error) {
        showAlert('Failed to load countries', 'error', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCountries();
  }, [showAlert]);

  const handleChange = (_, newValue) => {
    // Handle selection of existing countries or clearing
    if (newValue && typeof newValue === 'object') {
      // Selecting a country
      if (onChange) {
        if (returnValue === COUNTRY_RETURN_TYPES.NAME) {
          onChange(newValue.name || '');
        } else {
          onChange(newValue.id || '');
        }
      }
    } else if (newValue === null && onChange) {
      // Clearing the selection
      onChange('');
    }
  };

  const handleCreateNewCountry = (event) => {
    // Prevent any event bubbling that might trigger form submission
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    const trimmedInput = inputValue.trim();
    if (trimmedInput) {
      setIsAboutToCreateCountry(true);
      setPendingCountryName(trimmedInput);
      setShowConfirmModal(true);
      
      // Notify parent about country creation state
      if (onCreatingCountry) {
        onCreatingCountry(true);
      }
    }
  };

  const handleCreateCountry = async () => {
    try {
      setIsCreating(true);
      const newCountry = await organizationApi.createCountry({ 
        name: pendingCountryName 
      });
      
      // Add the new country to the countries list
      setCountries(prev => [...prev, newCountry]);
      
      // Select the newly created country
      if (onChange) {
        const valueToSend = returnValue === COUNTRY_RETURN_TYPES.NAME ? newCountry.name : newCountry.id;
        onChange(valueToSend);
      }
      
      showAlert(`Country "${newCountry.name}" created successfully`, 'success');
      setShowConfirmModal(false);
      setPendingCountryName('');
      setIsAboutToCreateCountry(false);
      
      // Notify parent that country creation is complete
      if (onCreatingCountry) {
        onCreatingCountry(false);
      }
    } catch (error) {
      showAlert(`Failed to create country: ${error.message}`, 'error', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleCancelCreate = () => {
    setShowConfirmModal(false);
    setPendingCountryName('');
    setInputValue('');
    setIsAboutToCreateCountry(false);
    
    // Notify parent that country creation is cancelled
    if (onCreatingCountry) {
      onCreatingCountry(false);
    }
  };

  // Find the selected country object from the ID or name
  const selectedCountry = countries.find(country => {
    if (returnValue === COUNTRY_RETURN_TYPES.NAME) {
      return country.name === value;
    } else {
      return country.id === value;
    }
  }) || null;
  
  // Check if input value would show any options in the dropdown
  const filteredOptionsCount = inputValue ? countries.filter(country =>
    country.name.toLowerCase().includes(inputValue.toLowerCase())
  ).length : 0;
  const isNewCountry = inputValue && filteredOptionsCount === 0 && !selectedCountry;
  
  // Sync input value with selected country
  useEffect(() => {
    if (selectedCountry && inputValue !== selectedCountry.name) {
      setInputValue(selectedCountry.name);
    } else if (!selectedCountry && !inputValue) {
      setInputValue('');
    }
  }, [selectedCountry, inputValue]);

  // Additional effect to handle newly created countries
  useEffect(() => {
    if (value && countries.length > 0) {
      const country = countries.find(c => {
        if (returnValue === COUNTRY_RETURN_TYPES.NAME) {
          return c.name === value;
        } else {
          return c.id === value;
        }
      });
      
      if (country && inputValue !== country.name) {
        setInputValue(country.name);
      }
    }
  }, [value, countries, returnValue, inputValue]);

  // Handle auto-focus for DataGrid edit cells
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      const focusInput = () => {
        const input = inputRef.current?.querySelector('input');
        if (input && document.contains(input)) {
          input.focus();
          // Don't select all text, just position cursor at end
          input.setSelectionRange(input.value.length, input.value.length);
          // Auto-open the dropdown after focusing
          setTimeout(() => {
            const popupButton = inputRef.current?.querySelector('.MuiAutocomplete-popupIndicator');
            if (popupButton) {
              popupButton.click();
            }
          }, 100);
          return true; // Successfully focused
        }
        return false; // Focus failed
      };
      
      // Try immediate focus first
      if (focusInput()) {
        return; // Success on first try
      }
      
      // Use requestAnimationFrame for better timing with DOM updates
      let rafId = null;
      let attempts = 0;
      const maxAttempts = 5; // Limit attempts to prevent infinite loops
      
      const tryFocusWithRAF = () => {
        if (attempts < maxAttempts && !focusInput()) {
          attempts++;
          rafId = requestAnimationFrame(tryFocusWithRAF);
        }
      };
      
      rafId = requestAnimationFrame(tryFocusWithRAF);
      
      return () => {
        if (rafId) {
          cancelAnimationFrame(rafId);
        }
      };
    }
  }, [autoFocus]);

  return (
    <FormControl 
      fullWidth={fullWidth} 
      {...props} 
      data-testid="country-selector"

    >
      <Autocomplete
        options={countries}
        loading={isLoading}
        disabled={disabled || isLoading}
        getOptionLabel={(option) => option?.name || ''}
        value={selectedCountry}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
        isOptionEqualToValue={(option, value) => option.id === value?.id}
        onOpen={onOpen}
        onClose={onClose}
        freeSolo
        openOnFocus
        selectOnFocus={false}
        clearOnBlur={false}
        handleHomeEndKeys
        filterOptions={(options, params) => {
          // If no input value and dropdown is opening, show all options
          if (!params.inputValue.trim()) {
            return options;
          }
          // Otherwise use default filtering
          return options.filter(option => 
            option.name.toLowerCase().includes(params.inputValue.toLowerCase())
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            ref={inputRef}
            label={label}
            required={required}
            autoFocus={autoFocus}
            placeholder="Search countries..."
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                const trimmedInput = inputValue.trim();
                // If there's input text and it would create a new country, prevent default behavior
                if (trimmedInput && isNewCountry) {
                  event.preventDefault();
                  event.stopPropagation();
                  // Don't clear the input, keep it for the user to see the + button
                  return false;
                }
              }
            }}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {isNewCountry && (
                      <Tooltip title={`Create new country "${inputValue}"`}>
                        <IconButton
                          type="button"
                          size="small"
                          onClick={handleCreateNewCountry}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          sx={{
                            color: '#2563eb',
                            padding: '4px',
                            marginRight: '4px',
                            '&:hover': {
                              backgroundColor: 'rgba(37, 99, 235, 0.1)'
                            }
                          }}
                        >
                          <FaPlus size={12} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                '& fieldset': {
                  borderColor: '#E2E8F0',
                },
                '&:hover fieldset': {
                  borderColor: '#2563eb',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#2563eb',
                }
              }
            }}
          />
        )}
        noOptionsText={
          isLoading ? "Loading..." : (
            <Box sx={{ p: 1 }}>
              <Typography variant="body2" color="text.secondary">
                No countries found.
              </Typography>
              {inputValue && (
                <Typography variant="body2" color="primary" sx={{ mt: 0.5 }}>
                  Click the + button to create "{inputValue}"
                </Typography>
              )}
            </Box>
          )
        }
      />
      
      {isNewCountry && (
        <FormHelperText sx={{ color: '#2563eb', fontWeight: 500, mt: 0.5 }}>
          Click the + button to create "{inputValue}" as a new country
        </FormHelperText>
      )}
      
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={handleCancelCreate}
        onConfirm={handleCreateCountry}
        title="Create New Country"
        message={`Do you want to create "${pendingCountryName}" as a new country?`}
        actionButtonText={isCreating ? "Creating..." : "Create"}
      />
    </FormControl>
  );
};

export default CountrySelector; 